import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import StepperContext from './stepper-context';
import StepperTitle from './components/stepper-title';
import StepperTitlesList from './components/stepper-titles-list';
import StepperContent from './components/stepper-content';
import StepperContentList from './components/stepper-content-list';

const { Stack } = designSystem;

const FADE_MS = 100;

const Stepper = ({ activeStepId, children }) => {
  const titlesListRef = useRef(null);
  const activeTitleRef = useRef(null);
  const titlesListWrapperRef = useRef(null);
  const crossfadeTimeoutRef = useRef(null);

  const [displayedStepId, setDisplayedStepId] = useState(activeStepId);
  const [prevStepId, setPrevStepId] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const centerActiveTitle = useCallback(() => {
    if (!titlesListRef.current || !activeTitleRef.current) return;

    const titlesListElement = titlesListRef.current;
    const activeTitleElement = activeTitleRef.current;
    const titlesListWrapperElement = titlesListWrapperRef.current;

    const titlesListRect = titlesListElement.getBoundingClientRect();
    const activeTitleRect = activeTitleElement.getBoundingClientRect();
    const listWrapperRect = titlesListWrapperElement.getBoundingClientRect();

    const activeCenter = activeTitleRect.left + activeTitleRect.width / 2 - titlesListRect.left;
    const listWrapperCenter = listWrapperRect.width / 2;
    const translateX = listWrapperCenter - activeCenter;

    if (isReady) {
      titlesListElement.style.transition = `transform 100ms ease-out`;
    }
    titlesListElement.style.transform = `translateX(${translateX}px)`;
  }, [isReady]);

  useEffect(() => {
    window.addEventListener('resize', centerActiveTitle);
    return () => window.removeEventListener('resize', centerActiveTitle);
  }, [centerActiveTitle]);

  useEffect(() => {
    centerActiveTitle();
  }, [centerActiveTitle, activeStepId]);

  // Enable transitions after the first paint to avoid animating on initial render
  useEffect(() => {
    // Defer to the next frame so initial transform (centering) applies without transition
    const id = requestAnimationFrame(() => setIsReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Crossfade logic when activeStepId changes
  useEffect(() => {
    if (activeStepId !== displayedStepId) {
      if (crossfadeTimeoutRef.current) {
        clearTimeout(crossfadeTimeoutRef.current);
      }

      setPrevStepId(displayedStepId);
      setDisplayedStepId(activeStepId);

      crossfadeTimeoutRef.current = setTimeout(() => {
        setPrevStepId(null);
      }, FADE_MS);
    }

    return () => {
      if (crossfadeTimeoutRef.current) clearTimeout(crossfadeTimeoutRef.current);
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [activeStepId]);

  return (
    <StepperContext.Provider
      value={{
        activeStepId,
        displayedStepId,
        prevStepId,
        activeTitleRef,
        titlesListRef,
        titlesListWrapperRef,
      }}
    >
      <div className="ws-stepper">
        <Stack gap="spacing-xs">{children}</Stack>
      </div>
    </StepperContext.Provider>
  );
};

Stepper.propTypes = {
  children: PropTypes.node.isRequired,
  activeStepId: PropTypes.string.isRequired,
};

Stepper.Title = StepperTitle;
Stepper.TitlesList = StepperTitlesList;
Stepper.Content = StepperContent;
Stepper.ContentList = StepperContentList;

export default Stepper;
